import IconSearch from '@apollo/icons/default/IconSearch.svg';
import { Button, FormControl, FormLabel, Input } from '@apollo/orbit';
import { debounce, mapValues } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { PersistedQueriesFilterFragment } from 'src/app/graph/routes';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  RouteConfig,
  RouteConfigParams,
} from 'src/lib/routeConfig/RouteConfig';

type PersistedQueryFilterParams = RouteConfigParams<
  typeof PersistedQueriesFilterFragment
>;

function convertToRoutePatch(params: PersistedQueryFilterParams) {
  return mapValues(params, (value) =>
    value === undefined ? RouteConfig.DELETE : value,
  );
}

export const PersistedQueryFilters = ({
  onChangeFilters,
}: {
  onChangeFilters: () => void;
}) => {
  const { currentPersistedQueriesListRoute } = useInternalGraphLinking();
  const params = useRouteParams(PersistedQueriesFilterFragment);

  const hasActiveFilters = Object.values(params).some(
    (value) => value !== undefined,
  );

  const history = useHistory();
  const location = useLocation();
  const setUrlFilter = useCallback(
    (filterParams: PersistedQueryFilterParams) => {
      history.push(
        currentPersistedQueriesListRoute.locationFrom({
          location,
          patch: convertToRoutePatch({
            ...params,
            ...filterParams,
          }),
        }),
      );
      onChangeFilters();
    },
    [
      currentPersistedQueriesListRoute,
      history,
      location,
      onChangeFilters,
      params,
    ],
  );

  const [nameSearch, setNameSearch] = useState(params.search || '');
  const setNameSearchDebounced = React.useMemo(() => {
    const debouncedSetQueryParams = debounce(
      (update: string) => {
        setUrlFilter({
          search: update || undefined,
        });
      },
      500,
      { leading: false, trailing: true },
    );
    return (update: React.SetStateAction<string>) => {
      setNameSearch((currentVal) => {
        const newVal =
          typeof update === 'function' ? update(currentVal) : update;
        debouncedSetQueryParams(newVal);
        return newVal;
      });
    };
  }, [setUrlFilter]);

  const onResetFilters = useCallback(() => {
    setUrlFilter({ search: undefined });
    setNameSearch('');
  }, [setUrlFilter]);

  return (
    <div className="flex space-x-2 pb-2 pt-3">
      <FormControl>
        <FormLabel className="sr-only">Search</FormLabel>
        <Input
          spellCheck={false}
          value={nameSearch}
          placeholder="Search"
          onChange={(e) => setNameSearchDebounced(e.target.value)}
          leftElement={<IconSearch className="size-4" />}
        />
      </FormControl>
      <Button
        variant="primary"
        isDisabled={!hasActiveFilters}
        onClick={onResetFilters}
      >
        Reset
      </Button>
    </div>
  );
};
